import CampaignPlatformType from '@/Enums/CampaignManagement/CampaignPlatformType.enum';
import CreativeType from '@/Enums/Creatives/CreativeType.enum';
import ItemStatus from '@/Enums/ItemStatus.enum';
import ScreenKey from '@/Enums/Screens/ScreenKey.enum';
import RefreshButton from '@/components/Buttons/RefreshButton';
import StatusList from '@/components/Icons/StatusList';
import TypeIcon from '@/components/Icons/TypeIcon';
import ConfirmationModal from '@/components/Modal/ConfirmationModal';
import SearchInput from '@/components/SearchInput';
import SelectInputPlaceholder from '@/components/Select/InputPlaceholder';
import IOSSwitch from '@/components/Switch';
import { CREATIVE_STATUSES, CREATIVE_STATUSES_TITLES, CREATIVE_TYPES_TITLES } from '@/constants/creativeLibrary';
import { numberToUsFormat } from '@/helpers/valueFormatter/numberFormatter.helpers';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectIsDV360CampaignManagementEnabled } from '@/store/selectors/currentWhitelabel.selectors';
import { selectIsAdmin, selectMeScreenAccess } from '@/store/selectors/me.selectors';
import { setFilters } from '@/store/slices/creativeSlice';
import { Add, KeyboardArrowDown } from '@mui/icons-material';
import PublicIcon from '@mui/icons-material/Public';
import { Box, Button, Checkbox, ListItemText, Menu, MenuItem, Select, selectClasses } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import debounce from 'debounce';
import { ChangeEvent, MouseEvent, useCallback, useMemo, useState } from 'react';
import FilterPopover from './FilterPopover';

interface IToolbarProps {
  rowCount: number;
  handleAddCreative: (platform: CampaignPlatformType) => void;
  handleRefresh: () => void;
  canArchive: boolean;
  handleArchive: (ids: number[], isArchived: boolean) => Promise<void>;
  selectedCreativeIds: GridRowSelectionModel;
  handleClearSelection: () => void;
}

const Toolbar = ({
  rowCount,
  handleAddCreative,
  handleRefresh,
  canArchive,
  handleArchive,
  selectedCreativeIds,
  handleClearSelection,
}: IToolbarProps) => {
  const dispatch = useAppDispatch();

  const isAdmin = useAppSelector(selectIsAdmin);
  const isArchived = useAppSelector(({ creative }) => creative.filters.isArchived);
  const [isBulkArchiveConfirmationOpen, setIsBulkArchiveConfirmationOpen] = useState(false);

  const handleArchivedChange = useCallback(() => {
    dispatch(setFilters({ isArchived: !isArchived }));
  }, [dispatch, isArchived]);

  const handleBulkArchiveConfirm = useCallback(async () => {
    await handleArchive(selectedCreativeIds as number[], !isArchived);
    setIsBulkArchiveConfirmationOpen(false);
  }, [handleArchive, selectedCreativeIds, isArchived]);

  const isDV360CampaignManagementEnabled = useAppSelector(selectIsDV360CampaignManagementEnabled);

  const [selectedPlatform, setSelectedPlatform] = useState<CampaignPlatformType>(CampaignPlatformType.Omnichannel);

  const [selectedTypes, setSelectedTypes] = useState<CreativeType[]>(Object.values(CreativeType));
  const [selectedStatuses, setSelectedStatuses] = useState<ItemStatus[]>(Object.values(CREATIVE_STATUSES));
  const [searchValue, setSearchValue] = useState<string>('');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const readOnly = useAppSelector(selectMeScreenAccess(ScreenKey.CREATIVE_LIBRARY));

  const handleSearch = useCallback(
    debounce(newSearch => {
      dispatch(setFilters({ search: newSearch }));
    }, 500),
    [dispatch],
  );

  const handleSearchValueChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      setSearchValue(value);
      handleSearch(value);
    },
    [handleSearch],
  );

  const handleTypeChange = useCallback(
    (types: CreativeType[]) => {
      setSelectedTypes(types);
      dispatch(setFilters({ types }));
    },
    [dispatch],
  );

  const handleStatusChange = useCallback(
    (statuses: ItemStatus[]) => {
      setSelectedStatuses(statuses);
      dispatch(setFilters({ statuses }));
    },
    [dispatch],
  );

  const handlePlatformChange = useCallback(
    (platform: CampaignPlatformType) => {
      if (!isDV360CampaignManagementEnabled) {
        return;
      }
      setSelectedPlatform(platform);
      dispatch(setFilters({ platform }));
    },
    [dispatch, isDV360CampaignManagementEnabled],
  );

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const handleMenuOpen = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (!isDV360CampaignManagementEnabled) {
        return;
      }
      setAnchorEl(event.currentTarget);
    },
    [setAnchorEl, isDV360CampaignManagementEnabled],
  );

  const handleAddClick = useCallback(
    (platform: CampaignPlatformType) => {
      handleAddCreative(platform);
      handleMenuClose();
    },
    [handleMenuClose, handleAddCreative],
  );

  const visibleTypes = useMemo(
    () =>
      Object.values(CreativeType).filter(
        type => isDV360CampaignManagementEnabled || type !== CreativeType.YOUTUBE_LINK,
      ),
    [isDV360CampaignManagementEnabled],
  );

  const handleSelectAllTypes = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      handleTypeChange(visibleTypes);
    },
    [handleTypeChange, visibleTypes],
  );

  const handleDeselectAllTypes = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      handleTypeChange([]);
    },
    [handleTypeChange],
  );

  return (
    <div className='mx-5'>
      <div className='flex justify-between items-center pt-5'>
        <p className='text-center text-[24px] font-semibold'>
          Total Creatives{' '}
          <span className='text-[20px] font-normal text-[#84818A]'>
            ({numberToUsFormat(rowCount, { maximumFractionDigits: 0 })})
          </span>
        </p>
      </div>
      <div className='flex flex-row justify-between mt-5 pb-[22px]'>
        <div className='flex flex-row w-[85%] gap-2 items-center'>
          <SearchInput
            className='w-[50%] xl:w-[35%]'
            value={searchValue}
            onChange={handleSearchValueChange}
            placeholder='Search Creative'
            autoComplete='off'
          />
          <FilterPopover />
          <Select
            startAdornment={<StatusList className='w-[20px] shrink-0' />}
            value={selectedStatuses}
            multiple
            displayEmpty
            renderValue={() => <SelectInputPlaceholder>Status</SelectInputPlaceholder>}
            sx={{
              height: '37px',
              [`& .${selectClasses.select}`]: {
                paddingLeft: '4px',
                paddingRight: 0,
              },
            }}
            className='w-[150px] min-h-[40px]'
            onChange={e => handleStatusChange(e.target.value as ItemStatus[])}
          >
            {CREATIVE_STATUSES.map(status => (
              <MenuItem key={status} value={status} sx={{ paddingLeft: '4px' }}>
                <Checkbox checked={selectedStatuses.includes(status)} />
                <ListItemText>{CREATIVE_STATUSES_TITLES[status]}</ListItemText>
              </MenuItem>
            ))}
          </Select>
          <Select
            startAdornment={<TypeIcon />}
            value={selectedTypes}
            multiple
            displayEmpty
            renderValue={() => <SelectInputPlaceholder>Type</SelectInputPlaceholder>}
            sx={{
              height: '37px',
              [`& .${selectClasses.select}`]: {
                paddingLeft: '4px',
                paddingRight: 0,
              },
            }}
            className='w-[150px] min-h-[40px]'
            onChange={e => handleTypeChange(e.target.value as CreativeType[])}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                px: 1,
                py: 0.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
              onClick={e => e.stopPropagation()}
            >
              <Button size='small' onClick={handleSelectAllTypes}>
                Select all
              </Button>

              <Button size='small' onClick={handleDeselectAllTypes}>
                Deselect all
              </Button>
            </Box>

            {visibleTypes.map(type => (
              <MenuItem key={type} value={type} sx={{ paddingLeft: '4px' }}>
                <Checkbox checked={selectedTypes.includes(type)} />
                <ListItemText className='capitalize'>{CREATIVE_TYPES_TITLES[type]}</ListItemText>
              </MenuItem>
            ))}
          </Select>
          {isAdmin && selectedPlatform !== CampaignPlatformType.Google && (
            <Button variant='outlined' onClick={handleArchivedChange} sx={{ height: '37px' }}>
              <span>Archived</span>
              <IOSSwitch sx={{ marginLeft: '7px' }} checked={isArchived} />
            </Button>
          )}
          {isDV360CampaignManagementEnabled && (
            <Select
              value={selectedPlatform}
              displayEmpty
              startAdornment={<PublicIcon />}
              renderValue={value => value || <SelectInputPlaceholder>Platform</SelectInputPlaceholder>}
              sx={{
                height: '37px',
                [`& .${selectClasses.select}`]: {
                  paddingLeft: '4px',
                  paddingRight: 0,
                },
              }}
              className='w-[150px] min-h-[40px]'
              onChange={e => handlePlatformChange(e.target.value as CampaignPlatformType)}
            >
              {Object.values(CampaignPlatformType).map(platform => (
                <MenuItem key={platform} value={platform} sx={{ paddingLeft: '4px' }}>
                  {platform}
                </MenuItem>
              ))}
            </Select>
          )}
        </div>
        <div className='flex gap-2 h-[40px]'>
          {isDV360CampaignManagementEnabled ? (
            <>
              <Button
                variant='contained'
                onClick={handleMenuOpen}
                sx={{ whiteSpace: 'nowrap' }}
                disabled={readOnly}
                endIcon={<KeyboardArrowDown />}
              >
                <Add sx={{ width: 20, height: 20, marginRight: '4px' }} />
                <span>Add New</span>
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                {Object.values(CampaignPlatformType).map(platform => (
                  <MenuItem key={platform} onClick={() => handleAddClick(platform)}>
                    {platform}
                  </MenuItem>
                ))}
              </Menu>
            </>
          ) : (
            <Button
              variant='contained'
              onClick={() => handleAddCreative(CampaignPlatformType.Omnichannel)}
              sx={{ whiteSpace: 'nowrap' }}
              disabled={readOnly}
            >
              <Add sx={{ width: 20, height: 20, marginRight: '4px' }} />
              <span>Add New</span>
            </Button>
          )}
          <RefreshButton onClick={handleRefresh} />
        </div>
      </div>
      {canArchive && selectedCreativeIds.length > 0 && (
        <div className='flex items-center justify-between bg-[#F8F8F8] rounded-[8px] px-[16px] py-[8px] mb-[10px]'>
          <span className='text-[14px] font-semibold'>{selectedCreativeIds.length} selected</span>
          <div className='flex gap-[10px]'>
            <Button size='small' variant='contained' onClick={() => setIsBulkArchiveConfirmationOpen(true)}>
              {isArchived ? 'Unarchive' : 'Archive'}
            </Button>
            <Button size='small' variant='outlined' onClick={handleClearSelection}>
              Clear
            </Button>
          </div>
        </div>
      )}
      {isBulkArchiveConfirmationOpen && (
        <ConfirmationModal
          open={isBulkArchiveConfirmationOpen}
          onClose={() => setIsBulkArchiveConfirmationOpen(false)}
          onConfirm={handleBulkArchiveConfirm}
          text={`Are you sure you want to ${isArchived ? 'unarchive' : 'archive'} ${selectedCreativeIds.length} creative${selectedCreativeIds.length === 1 ? '' : 's'}?`}
        />
      )}
    </div>
  );
};

export default Toolbar;
